import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/common/Button";
import { Modal } from "components/common/Modal";
import { RenameAffectedPlans } from "../RenameAffectedPlans";
import type { ApplyRenameToProjectModalProps } from "./types";

const Spinner = () => <Loader2 size={14} className="animate-spin" />;

export const ApplyRenameToProjectModal = ({
  isOpen,
  oldGroupLabel,
  newGroupLabel,
  plans,
  submittingAction,
  onApplyToAll,
  onApplyToCurrent,
  onCancel,
}: ApplyRenameToProjectModalProps) => {
  const { t } = useTranslation();

  const isSubmitting = submittingAction !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="lg"
      disableBackdropClose={isSubmitting}
      disableEscapeClose={isSubmitting}
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        {t("smartSurveyor.groupInspector.applyRenameTitle")}
      </h2>
      <RenameAffectedPlans
        oldGroupLabel={oldGroupLabel}
        newGroupLabel={newGroupLabel}
        plans={plans}
        // Merge wording stays parked here until it is settled; the server still reports willMerge
        showMergeWarning={false}
        // The preview leaves out the plan the estimator is standing on
        listsEveryPlan={false}
      />
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          className="whitespace-nowrap"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("smartSurveyor.common.cancel")}
        </Button>
        <Button
          className="whitespace-nowrap"
          onClick={onApplyToCurrent}
          disabled={isSubmitting}
          startAdornment={
            submittingAction === "currentPlan" ? <Spinner /> : undefined
          }
        >
          {t("smartSurveyor.groupInspector.applyRenameCurrentPlan")}
        </Button>
        <Button
          variant="primary"
          className="whitespace-nowrap"
          onClick={onApplyToAll}
          disabled={isSubmitting}
          startAdornment={
            submittingAction === "allPlans" ? <Spinner /> : undefined
          }
        >
          {t("smartSurveyor.groupInspector.applyRenameAllPlans")}
        </Button>
      </div>
    </Modal>
  );
};
